import React, { useState } from 'react';

function fmt(n) {
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
  return n.toLocaleString();
}

function getRiskColors(score) {
  if (score >= 70) return { color: 'var(--risk-high)', bg: 'var(--risk-high-bg)', border: 'var(--risk-high-border)' };
  if (score >= 40) return { color: 'var(--risk-medium)', bg: 'var(--risk-medium-bg)', border: 'var(--risk-medium-border)' };
  return { color: 'var(--risk-low)', bg: 'var(--risk-low-bg)', border: 'var(--risk-low-border)' };
}

export default function CondoCard({ condo, onClick }) {
  const [hovered, setHovered] = useState(false);
  const { color, bg, border } = getRiskColors(condo.risk_score);
  const openComplaints = condo.complaints.filter(c => c.status === 'Open').length;
  const balance = condo.financials.operating_fund_balance;
  const balanceOk = balance > 0;

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: hovered ? 'var(--bg-hover)' : 'var(--bg-card)',
        border: `1px solid ${hovered ? 'rgba(255,255,255,0.12)' : 'var(--border)'}`,
        borderRadius: 'var(--radius-lg)',
        padding: 20,
        cursor: 'pointer',
        transition: 'all var(--transition)',
        transform: hovered ? 'translateY(-2px)' : 'none',
        boxShadow: hovered ? 'var(--shadow-elevated)' : 'var(--shadow-card)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Risk accent bar */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: 2,
        background: color, opacity: condo.risk_score >= 40 ? 1 : 0.4,
      }} />

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3 style={{
            fontFamily: 'var(--font-display)',
            fontSize: 17, fontWeight: 400,
            color: 'var(--text-primary)',
            lineHeight: 1.25, marginBottom: 4,
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>
            {condo.name}
          </h3>
          <p style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
            {condo.address} · {condo.neighborhood}
          </p>
        </div>

        {/* Risk badge */}
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          background: bg, border: `1px solid ${border}`,
          borderRadius: 'var(--radius-sm)',
          padding: '6px 10px', marginLeft: 12, flexShrink: 0,
        }}>
          <span style={{ fontSize: 18, fontWeight: 700, color, lineHeight: 1, fontFamily: 'var(--font-body)' }}>
            {condo.risk_score}
          </span>
          <span style={{ fontSize: 9, fontWeight: 600, color, letterSpacing: 0.5, textTransform: 'uppercase', marginTop: 2 }}>
            Risk
          </span>
        </div>
      </div>

      {/* Divider */}
      <div style={{ height: 1, background: 'var(--border-subtle)', marginBottom: 14 }} />

      {/* Stats row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 14 }}>
        <Stat label="Units" value={condo.units} />
        <Stat label="Built" value={condo.year_built} />
        <Stat label="Complaints" value={condo.complaints.length} highlight={condo.complaints.length > 3} />
      </div>

      {/* Financial bar */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        background: 'var(--bg-elevated)', borderRadius: 'var(--radius-sm)',
        padding: '8px 12px',
      }}>
        <div>
          <div style={{ fontSize: 10, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 2 }}>
            Operating Balance
          </div>
          <div style={{ fontSize: 13, fontWeight: 600, color: balanceOk ? 'var(--risk-low)' : 'var(--risk-high)' }}>
            {balanceOk ? '+' : ''}{fmt(balance)}
          </div>
        </div>
        {condo.special_assessment && (
          <div style={{
            fontSize: 10, fontWeight: 600, letterSpacing: 0.5,
            color: 'var(--risk-high)',
            background: 'var(--risk-high-bg)',
            border: '1px solid var(--risk-high-border)',
            padding: '3px 8px', borderRadius: 100,
            textTransform: 'uppercase',
          }}>
            Special Assessment
          </div>
        )}
        {openComplaints > 0 && (
          <div style={{
            fontSize: 10, fontWeight: 600,
            color: 'var(--risk-medium)',
            background: 'var(--risk-medium-bg)',
            border: '1px solid var(--risk-medium-border)',
            padding: '3px 8px', borderRadius: 100,
          }}>
            {openComplaints} Open
          </div>
        )}
      </div>

      {/* Arrow */}
      <div style={{
        position: 'absolute', bottom: 20, right: 20,
        color: 'var(--text-tertiary)',
        transition: 'transform var(--transition), color var(--transition)',
        transform: hovered ? 'translateX(2px)' : 'none',
        ...(hovered ? { color: 'var(--accent)' } : {}),
      }}>
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M3 7h8M7 3l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>
    </div>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <div style={{
      background: 'var(--bg-elevated)', borderRadius: 'var(--radius-sm)', padding: '7px 10px',
    }}>
      <div style={{ fontSize: 10, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 14, fontWeight: 600, color: highlight ? 'var(--risk-medium)' : 'var(--text-primary)' }}>{value}</div>
    </div>
  );
}
